import { ReplayViewMode } from '@common/Replay/types';
import { Entity } from '@common/types/Entity';
import { isCaseInsensitiveEqual } from '@prophecy/utils/string';
import { useSearchParams } from 'react-router-dom';

import { CurrentEntity, EntityAction } from './types';

export const QUERY_PARAM_ENTITY_TYPE = 'entity';
export const QUERY_PARAM_ENTITY_NAME = 'name';
export const QUERY_PARAM_COMPONENT = 'component';
export const QUERY_PARAM_REPLAY_ID = 'replayId';
export const QUERY_PARAM_SHARE_ID = 'shareId';
export const QUERY_PARAM_REPLAY_MODE = 'replayMode';
export const QUERY_PARAM_ENTITY_ID = 'id';
export const QUERY_PARAM_ASSOCIATE_ENTITY = 'associateEntity';
export const QUERY_PARAM_FILE_PATH = 'filePath';
export const QUERY_PARAM_FROM_PIPELINE = 'fromPipeline';
export const QUERY_PARAM_IS_SPEC_FILE = 'isSpecFile';
export const QUERY_PARAM_ACTION = 'action';
export const QUERY_PARAM_ONBOARDING = 'onboarding';
export const QUERY_PARAM_ONBOARDING_TOUR = 'onboardingTour';
export const QUERY_PARAM_PACKAGE = 'package';
export const QUERY_PARAM_DATASET = 'dataset';

const UrlEntityToSqlEntity = {
  pipeline: Entity.Pipeline,
  model: Entity.Model,
  seed: Entity.Seed,
  macro: Entity.Macro,
  genericdatatest: Entity.GenericDataTest,
  job: Entity.Job,
  snapshot: Entity.Snapshot,
  markdowndocument: Entity.MarkdownDocument,
  [Entity.SingularDataTest.toLowerCase()]: Entity.SingularDataTest,
  dataset: Entity.Dataset,
  app: Entity.App,
  designdatamodel: Entity.ERD
};

export type CurrentEntityParams = ReturnType<typeof getCurrentEntityParams>;

export function getCurrentEntityParams(searchParams: URLSearchParams) {
  const queryEntity = (
    searchParams.get(QUERY_PARAM_ENTITY_TYPE) || ''
  ).toLowerCase() as keyof typeof UrlEntityToSqlEntity;
  const entity = queryEntity ? (UrlEntityToSqlEntity[queryEntity] as CurrentEntity['entity']) : undefined;

  const name = searchParams.get(QUERY_PARAM_ENTITY_NAME) || undefined;
  const component = searchParams.get(QUERY_PARAM_COMPONENT) || undefined;
  const replayId = searchParams.get(QUERY_PARAM_REPLAY_ID) || undefined;
  const shareId = searchParams.get(QUERY_PARAM_SHARE_ID) || undefined;
  const replayMode = (searchParams.get(QUERY_PARAM_REPLAY_MODE) || undefined) as ReplayViewMode;
  const id = searchParams.get(QUERY_PARAM_ENTITY_ID) || undefined;
  const associateEntity = searchParams.get(QUERY_PARAM_ASSOCIATE_ENTITY) || undefined;
  const filePath = searchParams.get(QUERY_PARAM_FILE_PATH) || undefined;
  const fromPipeline = searchParams.get(QUERY_PARAM_FROM_PIPELINE) || undefined;
  const isSpecFile = Boolean(searchParams.get(QUERY_PARAM_IS_SPEC_FILE));
  const action = (searchParams.get(QUERY_PARAM_ACTION) || undefined) as EntityAction | undefined;
  const onboarding = searchParams.get(QUERY_PARAM_ONBOARDING) || undefined;
  const onboardingTour = searchParams.get(QUERY_PARAM_ONBOARDING_TOUR) || undefined;
  const isPipelineAppView =
    (isCaseInsensitiveEqual(entity, Entity.Pipeline) && associateEntity) || isCaseInsensitiveEqual(entity, Entity.App);

  return {
    entity,
    filePath,
    replayMode,
    name,
    component,
    associateEntity,
    id,
    isSpecFile,
    action,
    fromPipeline,
    isPipelineAppView,
    replayId,
    shareId,
    onboardingTour,
    onboarding
  };
}

export function useCurrentEntity() {
  const [searchParams] = useSearchParams();
  return getCurrentEntityParams(searchParams);
}
